/**
 * apiBase.ts —— 部署到 CloudBase 后的接口桥接
 *
 * 老管理端调的是同源的 /wuying/* 与 /alliance-system/*（靠 Cookie 鉴权）。
 * 云端把这两个前缀按**完全相同的契约**实现了（admin-api 的 mgmt.js），
 * 所以这里只做三件事，业务代码一行都不用动：
 *   1. 前缀改写到云函数地址；
 *   2. 用会话令牌代替 Cookie（放 x-session 头，登录时收下、退出时丢掉）；
 *   3. 账号归属哪个分组，就把它记成当前分组（runtimeScope 读这个值）。
 *
 * SSE 在云函数上不常驻，这里给一个 15 秒轮询的空壳 EventSource，
 * 让 "revision" 事件照旧触发各模块刷新（App 自身另有 10 秒轮询兜底）。
 */
const CLOUD_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ||
  "https://alliance-web-d6g6wh6fh8d6d763d-1301836970.ap-shanghai.app.tcloudbase.com/admin";

const TOKEN_KEY = "alliance:session";
const SCOPE_KEY = "alliance:scope";
const SLUG_KEY = "alliance:slug";
const CHANNEL_KEY = "alliance:channel";

function readKey(key: string) {
  try { return window.localStorage.getItem(key) || ""; } catch { return ""; }
}
function writeKey(key: string, value: string) {
  try { value ? window.localStorage.setItem(key, value) : window.localStorage.removeItem(key); } catch { /* 隐私模式下忽略 */ }
}

export const getSessionToken = () => readKey(TOKEN_KEY);
export const setSessionToken = (token: string) => writeKey(TOKEN_KEY, token);
/** 账号归属的分组（scope_id）；data.ts 的 runtimeScope 优先读它 */
export const getScopeOverride = () => readKey(SCOPE_KEY);
export const setScopeOverride = (scope: string) => writeKey(SCOPE_KEY, scope);
/** 账号归属的联盟 slug（id）。管理端是多联盟的：数据接口按它取不同联盟的数据。
 *  登录时由响应 scope.alliance_id 写入；未登录/无账号时回落到 stellar-glory。 */
export const getAllianceSlug = () => readKey(SLUG_KEY) || "stellar-glory";
export const setAllianceSlug = (slug: string) => writeKey(SLUG_KEY, slug);
/** 账号归属联盟的采集渠道（huawei/tencent/…）。登录时由响应 scope.channel 写入；
 *  采集中心接口按它区分渠道，未登录时回落到 huawei。 */
export const getAllianceChannel = () => readKey(CHANNEL_KEY) || "huawei";
export const setAllianceChannel = (channel: string) => writeKey(CHANNEL_KEY, channel);

/** 会话信息（管理端按账号渲染分组筛选器 / 导出按钮用）。
 *  - scopes：该账号可用的分组（群星分组长只有本组；超管/御风管理员是全部；山河只有全盟）
 *  - ownGroup：账号绑定的分组名（'' = 未绑/超管）
 *  - ownScope：账号绑定的 scope（群星分组长的本组 scope）
 *  - locked：是否锁死本组（群星分组长 = true，不能让到别的组） */
export type SessionInfo = {
  scopes: Array<{ scope: string; label: string }>;
  ownGroup: string;
  ownScope: string;
  locked: boolean;
  allianceName: string;
};
const SESSION_INFO_KEY = "alliance:session-info";
export function getSessionInfo(): SessionInfo {
  try {
    const raw = window.localStorage.getItem(SESSION_INFO_KEY);
    if (raw) return JSON.parse(raw) as SessionInfo;
  } catch { /* ignore */ }
  return { scopes: [{ scope: "alliance", label: "联盟总体" }], ownGroup: "", ownScope: "alliance", locked: false, allianceName: "" };
}
export function setSessionInfo(info: Partial<SessionInfo>) {
  try { window.localStorage.setItem(SESSION_INFO_KEY, JSON.stringify(Object.assign(getSessionInfo(), info))); } catch { /* ignore */ }
}
export function clearSessionInfo() { writeKey(SESSION_INFO_KEY, ""); }

/** 当前选中/所属的分组名（导出「本组」用）。locked 时就是本组；否则取当前 scope 的组名。 */
export function currentGroupName(): string {
  const info = getSessionInfo();
  if (info.locked) return info.ownGroup;
  const cur = resolveRuntimeScope();
  const hit = info.scopes.find((s) => s.scope === cur);
  return hit && cur !== "alliance" ? hit.label : "";
}

const SCOPE_DIRECTORY = ["alliance", "qi-ji-ru-feng", "qi-xu-ru-lin", "qin-lue-ru-huo", "dong-ruo-lei-ting", "bu-dong-ru-shan"];

/** 当前内容口径（唯一的判定入口）。
 *  账号登录时写入的 `alliance:scope` 优先（服务端下发的 scope 一律信任，含各联盟自己的分组），
 *  其次才按 URL 路径兜底。 */
export function resolveRuntimeScope(): string {
  const stored = readKey(SCOPE_KEY);
  if (stored) return stored;
  return SCOPE_DIRECTORY.find((scope) => window.location.pathname.includes(`/${scope}/`)) || "alliance";
}

function rewrite(input: string) {
  if (/^https?:/i.test(input)) return input;
  if (input.startsWith("/wuying/") || input.startsWith("/alliance-system/")) return CLOUD_BASE + input;
  return input;
}

const nativeFetch = window.fetch.bind(window);

window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const original = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  const target = rewrite(original);
  const headers = new Headers(
    init?.headers || (typeof input === "object" && !(input instanceof URL) ? input.headers : undefined)
  );
  const token = getSessionToken();
  if (token && !headers.has("x-session")) headers.set("x-session", token);

  const response = await nativeFetch(target, { ...(init || {}), headers });

  // 登录：收下会话令牌与账号归属分组；退出：丢掉令牌
  if (original.startsWith("/wuying/login") && response.ok) {
    try {
      const payload = await response.clone().json();
      if (payload?.session) setSessionToken(String(payload.session));
      const scope = payload?.scope?.scope_id;
      if (scope) setScopeOverride(String(scope));
      const slug = payload?.scope?.alliance_id;
      if (slug) setAllianceSlug(String(slug));
      const channel = payload?.scope?.channel;
      if (channel) setAllianceChannel(String(channel));
      // 登录响应带 scopes（该账号可用分组）与 group（本组名），存下来给筛选器/导出用
      if (payload?.scope) {
        setSessionInfo({
          scopes: Array.isArray(payload.scope.scopes) ? payload.scope.scopes : getSessionInfo().scopes,
          ownGroup: String(payload.scope.group || ""),
          ownScope: String(scope || "alliance"),
          allianceName: String(payload.scope.alliance_name || ""),
          locked: false,
        });
      }
    } catch { /* 响应不是 JSON 就保持原样 */ }
  }
  if (original.startsWith("/wuying/logout")) {
    setSessionToken("");
    setAllianceSlug("");
    setAllianceChannel("");
    clearSessionInfo();
  }

  return response;
};

type RevisionListener = (event: MessageEvent) => void;

/** 云函数不适合长连接：用轮询空壳顶上 "revision" 事件，行为对调用方不变 */
class PollingEventSource {
  private listeners: Record<string, RevisionListener[]> = {};
  private timer: number;
  constructor(_url: string) {
    this.timer = window.setInterval(() => {
      const event = { data: JSON.stringify({ revision: Date.now() }) } as MessageEvent;
      (this.listeners.revision || []).forEach((fn) => fn(event));
    }, 15000);
  }
  addEventListener(type: string, listener: RevisionListener) {
    (this.listeners[type] = this.listeners[type] || []).push(listener);
  }
  removeEventListener(type: string, listener: RevisionListener) {
    this.listeners[type] = (this.listeners[type] || []).filter((fn) => fn !== listener);
  }
  close() { window.clearInterval(this.timer); }
}

(window as unknown as { EventSource: unknown }).EventSource = PollingEventSource;
